import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WarningIcon from "@mui/icons-material/Warning";
import { DateAndTimeGridCell } from "../../common/DateAndTimeGridCell";

const EditSubscriptionModal = ({ open, onClose, subscription, onSave }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  useEffect(() => {
    if (open && subscription?.subscriptionEndDate) {
      setSelectedDate(new Date(subscription.subscriptionEndDate));
    }
  }, [open, subscription]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const isSameDateSelected = () => {
    if (!selectedDate || !subscription?.subscriptionEndDate) return false;
    const currentDate = new Date(subscription.subscriptionEndDate);
    return selectedDate.toDateString() === currentDate.toDateString();
  };

  const handleSave = () => {
    if (selectedDate) {
      setShowConfirmation(true);
    }
  };

  const handleConfirmedSave = () => {
    const updatedSubscription = {
      ...subscription,
      subscriptionEndDate: selectedDate.toISOString(),
    };
    onSave(updatedSubscription);
    setShowConfirmation(false);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleClose = () => {
    setSelectedDate(null);
    setShowConfirmation(false);
    onClose();
  };

  const formatDate = (date) => {
    if (!date) return "No date selected";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const formatConfirmationDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(dateString));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open && !showConfirmation}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" component="div">
              Edit Subscription End Date
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 3 }}>
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Subscription Details
            </Typography>
            <Typography variant="body1">
              <strong>Subscription ID:</strong>{" "}
              {subscription?.subscriptionId || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Feature:</strong> {subscription?.featureName || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Service:</strong> {subscription?.name || "N/A"}
            </Typography>
            <Typography variant="body1" sx={{ marginTop: 2 }}>
              <strong>Current Subscription end date:</strong>
              {subscription?.subscriptionEndDate ? (
                <DateAndTimeGridCell value={subscription.subscriptionEndDate} />
              ) : (
                "N/A"
              )}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Select New End Date
            </Typography>

            <Box
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                mb: 2,
                backgroundColor: "#f8f9fa",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#e9ecef",
                  borderColor: "#1976d2",
                },
                transition: "all 0.2s ease-in-out",
              }}
              onClick={() => {
                const datePickerInput =
                  document.querySelector('[data-testid="date-picker-input"]') ||
                  document.querySelector('input[placeholder*="MM/dd/yyyy"]') ||
                  document.querySelector(".MuiInputBase-input");
                if (datePickerInput) {
                  datePickerInput.click();
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarTodayIcon color="primary" fontSize="small" />
                <Typography variant="body1">
                  <strong>Selected Date:</strong> {formatDate(selectedDate)}
                </Typography>
              </Box>
              {isSameDateSelected() && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ display: "block", mt: 1, fontStyle: "italic" }}
                >
                  ⚠️ This is the same as the current end date
                </Typography>
              )}
            </Box>

            <DatePicker
              label="Subscription End Date"
              value={selectedDate}
              onChange={handleDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": "date-picker-input",
                    readOnly: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    readOnly: true,
                    sx: {
                      cursor: "pointer",
                      "& .MuiInputBase-input": {
                        cursor: "pointer",
                      },
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (params.inputProps?.onClick) {
                      params.inputProps.onClick(e);
                    }
                  }}
                />
              )}
              minDate={subscription?.subscriptionStartDate}
              inputFormat="MM/dd/yyyy"
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Click on the calendar icon above to select a date
            </Typography>

            {isSameDateSelected() && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ mt: 1, display: "block", fontWeight: 600 }}
              >
                Please select a different date to enable saving
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!selectedDate || isSameDateSelected()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showConfirmation}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6" component="div">
              Confirm Subscription End Date Change
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to change the subscription end date?
          </Typography>

          {isSameDateSelected() && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff3e0",
                borderRadius: 1,
                border: "1px solid #ffb74d",
              }}
            >
              <Typography variant="body2" color="warning.dark">
                ⚠️ Warning: The selected date is the same as the current end
                date. No changes will be made.
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              p: 3,
              backgroundColor: "#f5f5f5",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              <strong>Subscription Details:</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>ID:</strong> {subscription?.subscriptionId || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Feature:</strong> {subscription?.featureName || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              <strong>Service:</strong> {subscription?.name || "N/A"}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#fff3e0",
                  borderRadius: 1,
                  border: "1px solid #ffb74d",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Current End Date:</strong>
                </Typography>
                <Typography variant="body1" color="warning.dark">
                  {formatConfirmationDate(subscription?.subscriptionEndDate)}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#e8f5e8",
                  borderRadius: 1,
                  border: "1px solid #66bb6a",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>New End Date:</strong>
                </Typography>
                <Typography variant="body1" color="success.dark">
                  {formatDate(selectedDate)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block", fontStyle: "italic" }}
          >
            This change will be applied immediately after confirmation.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCancelConfirmation}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedSave}
            variant="contained"
            color="primary"
          >
            Confirm Changes
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditSubscriptionModal;
